import { motion } from 'framer-motion'

/**
 * ServiceCard — colored gradient card with large icon.
 *
 * Props:
 *   icon        — emoji string  (default: '✨')
 *   title       — service name
 *   description — short description
 *   color       — hex accent color used for the gradient header
 *   index       — stagger animation delay
 *
 * Also supports legacy `service` object prop: { icon, title, description, color }
 */
export default function ServiceCard({
  icon:        iconProp,
  title:       titleProp,
  description: descProp,
  color:       colorProp,
  service,
  index = 0,
}) {
  const icon        = iconProp        ?? service?.icon        ?? '✨'
  const title       = titleProp       ?? service?.title       ?? ''
  const description = descProp        ?? service?.description ?? ''
  const color       = colorProp       ?? service?.color       ?? '#FF6BB5'

  // Build a light pastel from the accent color for the gradient card background
  const lightBg  = `${color}18`
  const midBg    = `${color}30`

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ delay: index * 0.1, duration: 0.55, ease: 'easeOut' }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group rounded-3xl overflow-hidden shadow-card hover:shadow-glow-primary transition-all duration-300 cursor-default"
    >
      {/* Gradient header with large icon */}
      <div
        className="relative flex flex-col items-center justify-center py-10 px-6 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${lightBg} 0%, ${midBg} 100%)`,
        }}
      >
        {/* Decorative circle behind icon */}
        <div
          className="absolute w-40 h-40 rounded-full blur-2xl opacity-40 pointer-events-none"
          style={{ background: color }}
        />

        {/* Icon */}
        <motion.span
          className="relative z-10 text-6xl drop-shadow-lg select-none"
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: index * 0.4 }}
          aria-hidden="true"
        >
          {icon}
        </motion.span>

        {/* Accent bottom edge */}
        <div
          className="absolute bottom-0 inset-x-0 h-1 opacity-50"
          style={{ background: color }}
        />
      </div>

      {/* Content */}
      <div className="bg-surface p-6">
        <h3 className="font-display text-ink text-xl mb-2">{title}</h3>
        <p className="text-ink-muted text-sm font-body leading-relaxed">{description}</p>

        {/* Hover accent bar */}
        <div
          className="mt-5 h-1 w-0 group-hover:w-12 rounded-full transition-all duration-500"
          style={{ background: color }}
        />
      </div>
    </motion.div>
  )
}
